import { AlertCircle, Bot, Clock } from "lucide-react";
import React from "react";
import { AI_CODE_REVIEWER_USERNAME } from "../constants";
import { Avatar } from "./Avatar";
import { Tooltip, TooltipBadgeContent } from "./Tooltip";

const isAiCodeReviewer = (reviewer) =>
	reviewer?.username === AI_CODE_REVIEWER_USERNAME;

const reviewTooltip = (message, badge = "Review") => (
	<TooltipBadgeContent badge={badge}>{message}</TooltipBadgeContent>
);

const AiReviewMissingStatus = () => (
	<Tooltip content={reviewTooltip("AI Code Review missing.", "AI")}>
		<span className="inline-flex items-center justify-center relative w-5 h-5 rounded-full bg-amber-500/10 ring-1 ring-amber-500/60 text-amber-600 dark:text-amber-400">
			<Bot className="w-3.5 h-3.5" />
			<AlertCircle className="absolute -right-1 -bottom-1 w-2.5 h-2.5 rounded-full bg-[var(--color-bg-primary)]" />
		</span>
	</Tooltip>
);

const HumanApprovalStatus = ({ reviewers, approvers, approvalsLeft }) => {
	if (approvers.length === 0) {
		if (reviewers.length === 0) {
			return (
				<Tooltip
					content={reviewTooltip("No human reviewers assigned.", "Review")}
				>
					<span className="inline-flex items-center relative">
						<Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
						<AlertCircle className="absolute -top-1 -right-1 w-2.5 h-2.5 text-red-500" />
					</span>
				</Tooltip>
			);
		}

		return (
			<Tooltip
				content={reviewTooltip("Waiting for human approvals.", "Review")}
			>
				<span className="inline-flex items-center">
					<Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
				</span>
			</Tooltip>
		);
	}

	const hasOneApproval = approvers.length === 1;
	const bgColor =
		approvalsLeft === 0
			? "bg-opacity-10 dark:bg-opacity-15 bg-green-500 dark:bg-green-500"
			: "bg-opacity-10 dark:bg-opacity-15 bg-yellow-500 dark:bg-yellow-500";
	const ringColor =
		approvalsLeft === 0
			? hasOneApproval
				? "ring-green-300 dark:ring-green-300"
				: "ring-green-500 dark:ring-green-500"
			: "ring-yellow-500 dark:ring-yellow-500";
	const borderColor =
		approvalsLeft === 0
			? hasOneApproval
				? "border-green-200 dark:border-green-200"
				: "border-green-500 dark:border-green-400"
			: "border-yellow-500 dark:border-yellow-400";

	const content = (
		<div
			className={`flex -space-x-2 rounded-full px-3 py-1 ring-1 ${bgColor} ${ringColor}`}
		>
			{approvers.map((reviewer) => (
				<Tooltip
					key={reviewer.username || reviewer.name}
					content={reviewTooltip(`Approved by ${reviewer.name}.`, "Approved")}
				>
					<Avatar
						src={reviewer.avatarUrl}
						alt={reviewer.name}
						size="sm"
						className={borderColor}
					/>
				</Tooltip>
			))}
		</div>
	);

	return approvalsLeft > 0 ? (
		<Tooltip content={reviewTooltip("More approvals are needed.", "Review")}>
			{content}
		</Tooltip>
	) : (
		content
	);
};

export const ApprovalStatus = ({
	reviewers,
	approvers,
	approvalsLeft,
	aiReviewMissing,
}) => {
	const humanApprovers = (approvers || []).filter(
		(reviewer) => !isAiCodeReviewer(reviewer),
	);
	const humanReviewers = (reviewers || []).filter(
		(reviewer) => !isAiCodeReviewer(reviewer),
	);
	const humanApprovalsLeft =
		typeof approvalsLeft === "number"
			? Math.max(0, approvalsLeft - (aiReviewMissing ? 1 : 0))
			: approvalsLeft;

	return (
		<span className="inline-flex items-center gap-2">
			{aiReviewMissing && <AiReviewMissingStatus />}
			<HumanApprovalStatus
				approvers={humanApprovers}
				reviewers={humanReviewers}
				approvalsLeft={humanApprovalsLeft}
			/>
		</span>
	);
};

export default ApprovalStatus;
